import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Twitter, Linkedin, Menu, X, ChevronDown } from "lucide-react";
import { ASSETS, NAV_LINKS, INDUSTRIES_MENU, SOCIAL_LINKS } from "@/data/content";

const IndustriesDropdown = () => {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        data-testid="header-nav-industries-button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 font-nunito text-[13px] font-bold uppercase tracking-[0.14em] text-white hover:text-[#ff6b63] transition-colors duration-150"
      >
        Industries
        <ChevronDown size={13} strokeWidth={2.5} className={`transition-transform duration-150 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          data-testid="header-industries-dropdown"
          className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-56"
        >
          <div className="rounded-[12px] bg-[#0b0b0c]/97 backdrop-blur-md border border-white/12 shadow-[0_20px_60px_rgba(2,2,2,0.55)] py-2">
            {INDUSTRIES_MENU.map((item) => (
              <Link
                key={item.label}
                to={item.href}
                data-testid={item.testId}
                onClick={() => setOpen(false)}
                className="block px-5 py-2.5 font-nunito text-[12px] font-bold uppercase tracking-[0.12em] text-white/85 hover:text-[#ff6b63] hover:bg-white/[0.05] transition-colors duration-150"
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const SiteHeader = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToAudit = (e) => {
    e.preventDefault();
    setMobileOpen(false);
    const el = document.getElementById("visibility-audit");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-[110px] sm:top-11 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled || mobileOpen
          ? "bg-[#0b0b0c]/95 backdrop-blur-sm shadow-[0_6px_24px_rgba(2,2,2,0.35)]"
          : "bg-gradient-to-b from-[rgba(2,2,2,0.55)] to-transparent"
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className={`flex items-center justify-between transition-[height] duration-200 ${scrolled ? "h-[68px]" : "h-[84px]"}`}>
          {/* Logo */}
          <a
            href="/"
            data-testid="header-logo-link"
            className="flex items-center shrink-0"
            aria-label="Slingshot SEO home"
          >
            <img
              src={ASSETS.logo}
              alt="Slingshot SEO"
              className="h-8 sm:h-9 w-auto"
              width="193"
              height="36"
            />
          </a>

          {/* Desktop nav */}
          <nav data-testid="header-nav" className="hidden lg:flex items-center gap-7" aria-label="Primary">
            {NAV_LINKS.map((link) => (
              <span key={link.label} className="flex items-center gap-7">
                {link.internal ? (
                  <Link
                    to={link.internal}
                    data-testid={link.testId}
                    className="font-nunito text-[13px] font-bold uppercase tracking-[0.14em] text-white hover:text-[#ff6b63] transition-colors duration-150"
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    href={link.href}
                    data-testid={link.testId}
                    className="font-nunito text-[13px] font-bold uppercase tracking-[0.14em] text-white hover:text-[#ff6b63] transition-colors duration-150"
                  >
                    {link.label}
                  </a>
                )}
                {link.label === "Services" && <IndustriesDropdown />}
              </span>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            <div className="flex items-center gap-3 pr-4 border-r border-white/20">
              <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noopener noreferrer" data-testid="header-social-facebook-link" aria-label="Follow on Facebook" className="text-white/70 hover:text-white transition-colors duration-150">
                <Facebook size={16} strokeWidth={1.75} />
              </a>
              <a href={SOCIAL_LINKS.twitter} target="_blank" rel="noopener noreferrer" data-testid="header-social-twitter-link" aria-label="Follow on Twitter" className="text-white/70 hover:text-white transition-colors duration-150">
                <Twitter size={16} strokeWidth={1.75} />
              </a>
              <a href={SOCIAL_LINKS.linkedin} target="_blank" rel="noopener noreferrer" data-testid="header-social-linkedin-link" aria-label="Follow on LinkedIn" className="text-white/70 hover:text-white transition-colors duration-150">
                <Linkedin size={16} strokeWidth={1.75} />
              </a>
            </div>
            <a
              href="#visibility-audit"
              onClick={scrollToAudit}
              data-testid="header-cta-button"
              className="inline-flex items-center rounded-[10px] bg-[#ee2e24] px-4 py-2.5 font-nunito text-[12px] font-extrabold uppercase tracking-[0.1em] text-white shadow-[0_10px_30px_rgba(238,46,36,0.25)] hover:bg-[#d92820] active:scale-[0.98] transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-[rgba(238,46,36,0.35)] focus:ring-offset-2"
            >
              Free Visibility Audit
            </a>
          </div>

          {/* Mobile toggle */}
          <button
            type="button"
            data-testid="header-mobile-menu-button"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-[10px] border border-white/25 text-white hover:text-[#ff6b63] transition-colors duration-150"
          >
            {mobileOpen ? <X size={20} strokeWidth={1.75} /> : <Menu size={20} strokeWidth={1.75} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div data-testid="header-mobile-menu" className="lg:hidden bg-[#0b0b0c] border-t border-white/10 shadow-[0_20px_40px_rgba(2,2,2,0.5)]">
          <nav className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex flex-col" aria-label="Mobile">
            {NAV_LINKS.map((link) => (
              <span key={link.label} className="contents">
                {link.internal ? (
                  <Link
                    to={link.internal}
                    onClick={() => setMobileOpen(false)}
                    data-testid={`mobile-${link.testId}`}
                    className="py-3 font-nunito text-[13px] font-bold uppercase tracking-[0.14em] text-white border-b border-white/10 hover:text-[#ff6b63] transition-colors duration-150"
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    href={link.href}
                    data-testid={`mobile-${link.testId}`}
                    className="py-3 font-nunito text-[13px] font-bold uppercase tracking-[0.14em] text-white border-b border-white/10 hover:text-[#ff6b63] transition-colors duration-150"
                  >
                    {link.label}
                  </a>
                )}
                {link.label === "Services" && (
                  <div className="py-3 border-b border-white/10" data-testid="mobile-industries-section">
                    <p className="font-nunito text-[13px] font-bold uppercase tracking-[0.14em] text-white/60">Industries</p>
                    <div className="mt-2 grid grid-cols-2 gap-x-4">
                      {INDUSTRIES_MENU.map((item) => (
                        <Link
                          key={item.label}
                          to={item.href}
                          onClick={() => setMobileOpen(false)}
                          data-testid={`mobile-${item.testId}`}
                          className="py-1.5 font-nunito text-[12px] font-bold uppercase tracking-[0.1em] text-white/85 hover:text-[#ff6b63] transition-colors duration-150"
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </span>
            ))}
            <a
              href="#visibility-audit"
              onClick={scrollToAudit}
              data-testid="mobile-header-cta-button"
              className="mt-4 inline-flex justify-center items-center rounded-[10px] bg-[#ee2e24] px-4 py-3 font-nunito text-[12px] font-extrabold uppercase tracking-[0.1em] text-white hover:bg-[#d92820] transition-colors duration-150"
            >
              Free Visibility Audit
            </a>
            <div className="flex items-center gap-5 pt-4 justify-center">
              <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="text-white/70"><Facebook size={18} strokeWidth={1.75} /></a>
              <a href={SOCIAL_LINKS.twitter} target="_blank" rel="noopener noreferrer" aria-label="Twitter" className="text-white/70"><Twitter size={18} strokeWidth={1.75} /></a>
              <a href={SOCIAL_LINKS.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="text-white/70"><Linkedin size={18} strokeWidth={1.75} /></a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};
